import React, { useEffect, useState } from 'react';
import api, { unwrap } from '../utils/api';

// Boundary timestamps in the browser's local TZ so "today" tracks the
// admin's wall clock, not UTC. Week starts Sunday (matches the Jewish
// week and the typical US business calendar).
function computeBoundaries() {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekStart = new Date(todayStart);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const yearStart = new Date(now.getFullYear(), 0, 1);
  return {
    todayStart: todayStart.toISOString(),
    weekStart: weekStart.toISOString(),
    monthStart: monthStart.toISOString(),
    yearStart: yearStart.toISOString(),
  };
}

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

function PeriodCard({ label, stats, refunds }) {
  const orders = stats?.orderCount || 0;
  const revenue = stats?.revenue || 0;
  const free = stats?.freeCount || 0;
  const refundCount = refunds?.refundCount || 0;
  const refunded = refunds?.refundedAmount || 0;
  return (
    <div className="card">
      <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, marginTop: 4 }}>{orders}</div>
      <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>order{orders === 1 ? '' : 's'}</div>
      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #eef2f6', display: 'grid', gap: 4, fontSize: 13 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: '#666' }}>Revenue</span>
          <strong>{money(revenue)}</strong>
        </div>
        {free > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#666' }}>
            <span>Free (100%-off coupon)</span>
            <span>{free}</span>
          </div>
        )}
        {refundCount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#b42318' }}>
            <span>Refunds</span>
            <span>{refundCount} · {money(refunded)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  async function load() {
    setLoading(true); setErr('');
    try {
      const r = await api.get('/analytics/overview', { params: computeBoundaries() });
      setData(unwrap(r));
    } catch (e) {
      setErr(e?.response?.data?.body || e.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  if (loading) return <><h1>Analytics</h1><p>Loading…</p></>;
  if (err) return <><h1>Analytics</h1><p style={{ color: '#b42318' }}>{err}</p></>;
  if (!data) return null;

  const { periods, refunds, averageOrderValue, topCoupons, byCategory, liveModeStart } = data;

  // Sort categories so the busiest one is at the top — most useful info first.
  const categoryEntries = Object.entries(byCategory || {}).sort((a, b) => {
    const aTotal = a[1].templates.reduce((s, t) => s + t.count, 0);
    const bTotal = b[1].templates.reduce((s, t) => s + t.count, 0);
    return bTotal - aTotal;
  });

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ margin: 0 }}>Analytics</h1>
        <button className="btn small secondary" onClick={load}>Refresh</button>
      </div>
      <p style={{ color: '#666', marginTop: 6, fontSize: 13 }}>
        Today / week / month / year are in your local time (week starts Sunday).
        Orders shown count paid + free-coupon orders; refunds are tracked separately.
      </p>
      {liveModeStart && (
        <p style={{ color: '#0a5fb4', marginTop: 0, fontSize: 13 }}>
          Counting only orders from <strong>{new Date(liveModeStart).toLocaleString()}</strong> onward (sandbox orders excluded).
        </p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginTop: 18 }}>
        <PeriodCard label="Today" stats={periods.today} refunds={refunds.today} />
        <PeriodCard label="This week" stats={periods.week} refunds={refunds.week} />
        <PeriodCard label="This month" stats={periods.month} refunds={refunds.month} />
        <PeriodCard label="This year" stats={periods.year} refunds={refunds.year} />
      </div>

      <div className="card" style={{ marginTop: 22 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 24 }}>
          <div>
            <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>All-time orders</div>
            <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{periods.allTime?.orderCount || 0}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>All-time revenue</div>
            <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{money(periods.allTime?.revenue)}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Average order value</div>
            <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{money(averageOrderValue?.value)}</div>
            <div style={{ fontSize: 12, color: '#888' }}>over {averageOrderValue?.n || 0} paid orders</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Refunds (all-time)</div>
            <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4, color: refunds.allTime?.refundCount ? '#b42318' : '#1a2533' }}>
              {refunds.allTime?.refundCount || 0}
            </div>
            <div style={{ fontSize: 12, color: '#888' }}>{money(refunds.allTime?.refundedAmount)} refunded</div>
          </div>
        </div>
      </div>

      <h2 style={{ marginTop: 32, marginBottom: 12 }}>Top templates by category</h2>
      {categoryEntries.length === 0 ? (
        <p style={{ color: '#888' }}>No template sales yet.</p>
      ) : (
        categoryEntries.map(([catId, { categoryName, templates }]) => {
          const total = templates.reduce((s, t) => s + t.count, 0);
          return (
            <div className="card" key={catId} style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <h3 style={{ margin: 0 }}>{categoryName}</h3>
                <span style={{ color: '#888', fontSize: 13 }}>{total} order{total === 1 ? '' : 's'} across {templates.length} template{templates.length === 1 ? '' : 's'}</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid #eef2f6', fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    <th style={{ padding: '8px 4px', width: 50 }}>#</th>
                    <th style={{ padding: '8px 4px' }}>Template</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', width: 90 }}>Orders</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', width: 110 }}>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t, i) => (
                    <tr key={String(t.productId)} style={{ borderBottom: '1px solid #f5f7fa' }}>
                      <td style={{ padding: '8px 4px', color: '#888' }}>{i + 1}</td>
                      <td style={{ padding: '8px 4px' }}>{t.productName}</td>
                      <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600 }}>{t.count}</td>
                      <td style={{ padding: '8px 4px', textAlign: 'right' }}>{money(t.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })
      )}

      <h2 style={{ marginTop: 32, marginBottom: 12 }}>Top coupons</h2>
      {(!topCoupons || topCoupons.length === 0) ? (
        <p style={{ color: '#888' }}>No coupons have been used yet.</p>
      ) : (
        <div className="card">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #eef2f6', fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <th style={{ padding: '8px 4px' }}>Code</th>
                <th style={{ padding: '8px 4px', textAlign: 'right', width: 90 }}>Uses</th>
                <th style={{ padding: '8px 4px', textAlign: 'right', width: 150 }}>Total discount given</th>
              </tr>
            </thead>
            <tbody>
              {topCoupons.map((c) => (
                <tr key={c.code} style={{ borderBottom: '1px solid #f5f7fa' }}>
                  <td style={{ padding: '8px 4px', fontWeight: 600 }}>{c.code}</td>
                  <td style={{ padding: '8px 4px', textAlign: 'right' }}>{c.uses}</td>
                  <td style={{ padding: '8px 4px', textAlign: 'right' }}>{money(c.totalDiscount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
